import FleetModal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import { SubmitRisalahPayload } from '@/libs/fleet-api/services/nap';
// import { FileData } from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import { useMutation } from '@tanstack/react-query';
import {
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	// Upload,
	// UploadFile,
} from 'antd';
// import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FC, Fragment, useMemo, useState } from 'react';
import { MasterData } from '@/libs/fleet-api/services/master-data';
import Radio from 'antd/es/radio/radio';
import { useModals } from '@/components/modals/ModalProvider';

export type CovenantPayload = SubmitRisalahPayload['covenants'][number];
type CovenantModalConfig =
	| {
			type: 'new';
	  }
	| {
			type: 'edit';
			record: CovenantPayload;
	  };

export const useCovenantModal = () =>
	useAsyncModal<CovenantModalConfig, CovenantPayload>();

type CovenantModalProps = ReturnType<typeof useCovenantModal> & {
	customerId: string;
	prospectId: string;
	categoryOptions: MasterData[];
	priorityOptions: MasterData[];
	repeatFrequencyOptions: MasterData[];
	customRepeatFrequencyOptions: MasterData[];
	monitoringType: MasterData[];
	furtherAnalysisOptions: MasterData[];
	monthOptions: MasterData[];
	positionOptions: MasterData[];
	dayOfWeekOptions: MasterData[];
};

const REPEAT_CUSTOM = { id: 6, value: 'Custom' } as const satisfies MasterData;

enum REPEAT_FREQUENCY {
	Day = 1,
	Week,
	Month,
	Year,
}

const CovenantModal: FC<CovenantModalProps> = ({
	// customerId,
	// prospectId,
	categoryOptions = [],
	priorityOptions = [],
	repeatFrequencyOptions = [],
	customRepeatFrequencyOptions = [],
	furtherAnalysisOptions = [],
	monitoringType = [],
	monthOptions = [],
	positionOptions = [],
	dayOfWeekOptions = [],
	data,
	modal,
	close,
}) => {
	const { openPreviewModal } = useModals();
	const { form, fieldName, value } = useForm<CovenantPayload>();

	const customRepeatType = useMemo(() => {
		const id = value?.repeatFrequencyCustom?.repeatFrequencyId;
		if (id === 1) return 'day';
		if (id === 2) return 'week';
		if (id === 3) return 'month';
		if (id === 4) return 'year';
		return '';
	}, [value?.repeatFrequencyCustom?.repeatFrequencyId]);

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const [on, setOn] = useState(true);

	return (
		<FleetModal
			{...modal}
			width={940}
			closable={false}
			buttonPosition="end"
			okText="SAVE"
			cancelText="BACK"
			onCancel={() => {
				close(data?.type === 'edit' ? data.record : undefined);
			}}
			onOk={form.submit}
			okButtonProps={{
				disabled: uploadMutation.isPending,
			}}
			cancelButtonProps={{
				disabled: uploadMutation.isPending,
			}}
			afterOpenChange={(isOpen) => {
				if (!isOpen) {
					form.resetFields();
				} else if (data?.type === 'edit') {
					form.setFieldsValue({
						...data.record,
						repeatFrequencyId: data.record.repeatFrequencyCustom
							? REPEAT_CUSTOM.id
							: data.record.repeatFrequencyId,
					});

					setOn(
						Boolean(data.record.repeatFrequencyCustom?.onPattern.dayOfMonth),
					);
				}
			}}
		>
			<div style={{ marginBottom: 20 }}>
				<FleetTitle>Add Data Covenant</FleetTitle>
			</div>
			<Form
				form={form}
				{...ANTD_DEFAULT_PROPS.FORM}
				onFinish={(values) => close(values)}
			>
				<Form.Item
					name={fieldName('covenant')}
					label="Covenant"
					rules={[rule.required()]}
					labelCol={{ span: 6 }}
					wrapperCol={{ span: 18 }}
				>
					<Input.TextArea rows={3} />
				</Form.Item>
				<Divider />
				<Row gutter={20}>
					<Col span={12}>
						<Form.Item
							name={fieldName('categoryId')}
							label="Kategori"
							rules={[rule.required()]}
							labelCol={{ span: 12 }}
							wrapperCol={{ span: 12 }}
						>
							<Select
								options={categoryOptions?.map((option) => {
									return {
										label: option.value,
										value: option.id,
									};
								})}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('priorityId')}
							label="Priority"
							rules={[rule.required()]}
							labelCol={{ span: 12 }}
							wrapperCol={{ span: 12 }}
						>
							<Select
								options={priorityOptions?.map((option) => {
									return {
										label: option.value,
										value: option.id,
									};
								})}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={20}>
					<Col span={12}>
						<Form.Item
							name={fieldName('repeatFrequencyId')}
							label="Repeat"
							rules={[rule.required()]}
							labelCol={{ span: 12 }}
							wrapperCol={{ span: 12 }}
						>
							<Select
								options={repeatFrequencyOptions?.map((option) => {
									return {
										label: option.value,
										value: option.id,
									};
								})}
								onChange={() => {
									form.resetFields([fieldName('repeatFrequencyCustom')]);
								}}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('monitoringTypeId')}
							label="Jenis Monitoring"
							rules={[rule.required()]}
							labelCol={{ span: 12 }}
							wrapperCol={{ span: 12 }}
						>
							<Select
								options={monitoringType?.map((option) => {
									return {
										label: option.value,
										value: option.id,
									};
								})}
							/>
						</Form.Item>
					</Col>
				</Row>
				{value?.repeatFrequencyId === REPEAT_CUSTOM.id && (
					<div style={{ marginTop: 20 }}>
						<div style={{ marginBottom: 20 }}>
							<FleetTitle>Repeat Custom</FleetTitle>
						</div>
						<Form.Item
							label="Repeat Every"
							required
							labelCol={{ span: 6 }}
							wrapperCol={{ span: 12 }}
						>
							<Row gutter={20}>
								<Col flex="none">
									<Form.Item
										name={fieldName(['repeatFrequencyCustom', 'repeatValue'])}
										rules={[
											rule.required(),
											{
												validator: (_, number) => {
													const { repeatFrequencyId } =
														value.repeatFrequencyCustom ?? {};

													const limit = {
														[REPEAT_FREQUENCY.Day]: 365,
														[REPEAT_FREQUENCY.Week]: 156,
														[REPEAT_FREQUENCY.Month]: 36,
														[REPEAT_FREQUENCY.Year]: 3,
													}[repeatFrequencyId as REPEAT_FREQUENCY];

													if (repeatFrequencyId && number > limit) {
														return Promise.reject(`Maksimal ${limit}`);
													}

													return Promise.resolve();
												},
											},
										]}
										noStyle
									>
										<InputNumber
											min={0}
											style={{ width: 100 }}
										/>
									</Form.Item>
								</Col>
								<Col flex="auto">
									<Form.Item
										name={fieldName([
											'repeatFrequencyCustom',
											'repeatFrequencyId',
										])}
										rules={[rule.required()]}
										noStyle
									>
										<Select
											options={customRepeatFrequencyOptions
												?.sort((a, b) => a.id - b.id)
												?.map((option) => {
													return {
														label: option.value,
														value: option.id,
													};
												})}
										/>
									</Form.Item>
								</Col>
							</Row>
						</Form.Item>
						{customRepeatType && customRepeatType !== 'day' && (
							<Fragment>
								<Divider />
								{customRepeatType === 'week' ? (
									<Row
										gutter={20}
										align="middle"
									>
										<Col span={6}>On</Col>
										<Col flex="none">
											<Form.Item
												name={fieldName([
													'repeatFrequencyCustom',
													'onThePattern',
													'dayOfWeekId',
												])}
												rules={[rule.required()]}
											>
												<Select
													style={{ width: 150 }}
													placeholder="Hari"
													options={dayOfWeekOptions
														?.sort((a, b) => a.id - b.id)
														?.map((option) => {
															return {
																label: option.value,
																value: option.id,
															};
														})}
												/>
											</Form.Item>
										</Col>
									</Row>
								) : (
									<Fragment>
										<Row
											gutter={20}
											align="middle"
										>
											<Col span={6}>
												<Radio
													checked={on}
													onChange={(e) => {
														if (e.target.checked) {
															setOn(true);
															form.resetFields([
																fieldName([
																	'repeatFrequencyCustom',
																	'onThePattern',
																]),
															]);
															form.validateFields([
																'repeatFrequencyCustom',
																'onPattern',
															]);
														}
													}}
												>
													On
												</Radio>
											</Col>
											{customRepeatType === 'year' && (
												<Col flex="none">
													<Form.Item
														name={fieldName([
															'repeatFrequencyCustom',
															'onPattern',
															'monthId',
														])}
														rules={on ? [rule.required()] : []}
													>
														<Select
															disabled={!on}
															style={{ width: 150 }}
															placeholder="Bulan"
															options={monthOptions
																?.sort((a, b) => a.id - b.id)
																?.map((option) => {
																	return {
																		label: option.value,
																		value: option.id,
																	};
																})}
														/>
													</Form.Item>
												</Col>
											)}
											<Col flex="none">
												<Form.Item
													name={fieldName([
														'repeatFrequencyCustom',
														'onPattern',
														'dayOfMonth',
													])}
													rules={on ? [rule.required()] : []}
												>
													<InputNumber
														min={1}
														max={
															value?.repeatFrequencyCustom?.onPattern?.monthId
																? dayjs()
																		.set(
																			'month',
																			value.repeatFrequencyCustom.onPattern
																				.monthId - 1,
																		)
																		.daysInMonth()
																: undefined
														}
														disabled={!on}
														style={{ width: 60 }}
													/>
												</Form.Item>
											</Col>
										</Row>
										<Divider />
										<Row
											gutter={20}
											align="middle"
										>
											<Col span={6}>
												<Radio
													checked={!on}
													onChange={(e) => {
														if (e.target.checked) {
															setOn(false);
															form.resetFields([
																fieldName([
																	'repeatFrequencyCustom',
																	'onPattern',
																]),
															]);
															form.validateFields([
																'repeatFrequencyCustom',
																'onThePattern',
															]);
														}
													}}
												>
													On The
												</Radio>
											</Col>
											<Col flex="none">
												<Form.Item
													name={fieldName([
														'repeatFrequencyCustom',
														'onThePattern',
														'positionId',
													])}
													rules={!on ? [rule.required()] : []}
												>
													<Select
														style={{ width: 150 }}
														disabled={on}
														placeholder="Position"
														options={positionOptions?.map((option) => {
															return {
																label: option.value,
																value: option.id,
															};
														})}
													/>
												</Form.Item>
											</Col>
											<Col flex="none">
												<Form.Item
													name={fieldName([
														'repeatFrequencyCustom',
														'onThePattern',
														'dayOfWeekId',
													])}
													rules={!on ? [rule.required()] : []}
												>
													<Select
														style={{ width: 150 }}
														placeholder="Hari"
														disabled={on}
														options={dayOfWeekOptions
															?.sort((a, b) => a.id - b.id)
															?.map((option) => {
																return {
																	label: option.value,
																	value: option.id,
																};
															})}
													/>
												</Form.Item>
											</Col>
											{customRepeatType === 'year' && (
												<Fragment>
													<Col flex="none">of</Col>
													<Col flex="none">
														<Form.Item
															name={fieldName([
																'repeatFrequencyCustom',
																'onThePattern',
																'monthId',
															])}
															rules={!on ? [rule.required()] : []}
														>
															<Select
																style={{ width: 150 }}
																placeholder="Bulan"
																disabled={on}
																options={monthOptions
																	?.sort((a, b) => a.id - b.id)
																	?.map((option) => {
																		return {
																			label: option.value,
																			value: option.id,
																		};
																	})}
															/>
														</Form.Item>
													</Col>
												</Fragment>
											)}
										</Row>
									</Fragment>
								)}
							</Fragment>
						)}
					</div>
				)}
				<Divider />
				<Row gutter={20}>
					<Col span={12}>
						<Form.Item
							name={fieldName('furtherAnalysisId')}
							label="Analisa Lebih Lanjut"
							rules={[rule.required()]}
							labelCol={{ span: 12 }}
							wrapperCol={{ span: 12 }}
						>
							<Select
								options={furtherAnalysisOptions?.map((option) => {
									return {
										label: option.value,
										value: option.id,
									};
								})}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={20}>
					<Col span={12}>
						<Form.Item
							label="View Dokumen"
							name={fieldName('covenantDocument')}
						>
							{data?.type === 'edit' && data.record.covenantDocument && (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openPreviewModal(data.record.covenantDocument);
									}}
								>
									{data.record.covenantDocument.name}
								</a>
							)}
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('dueDate')}
							label="Due Date"
							labelCol={{ span: 12 }}
							wrapperCol={{ span: 12 }}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) =>
								value && dayjs(value).utc(true).toISOString()
							}
						>
							<DatePicker
								disabledDate={(current) =>
									current && current < dayjs().startOf('day')
								}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
			</Form>
		</FleetModal>
	);
};

export default CovenantModal;
